import { stripSlash } from "../shared/string.utils";
export class UrlBuilder {
  #shouldDecode = false;
  #url;
  constructor(url) {
    this.#url = new URL(url);
  }

  /**
   * Sets the URL of the UrlBuilder.
   * @param {string} url - The URL to set.
   * @returns {this} The UrlBuilder instance for chaining.
   */
  setUrl(url) {
    this.#url = new URL(url);
    return this;
  }

  /**
   * Handles query parameters of the URL by applying a callback function.
   * @param {function} cb - A callback function that takes the URL's searchParams as an argument and can modify them.
   * @returns {this} The UrlBuilder instance for chaining.
   */
  handleQueryParams(cb) {
    cb(this.searchParams);
    return this;
  }

  /**
   * Sets the path of the URL.
   * @param {string} value - The path to set.
   * @param {Object} [options={}] - Options for setting the path.
   * @param {StripSlashPosition | null} [options.stripSlash] - Position to strip slashes from the path.
   * @returns {this} The UrlBuilder instance for chaining.
   */
  setPathname(value) {
    this.#url.pathname = stripSlash(value, "start");
    return this;
  }

  /**
   * Appends a path segment to the existing path.
   * @param {string} value - The path segment to append.
   * @returns {this} The UrlBuilder instance for chaining.
   */
  appendPathname(value) {
    const trailingSlash = this.#url.pathname.endsWith("/") ? "" : "/";
    this.#url.pathname = `${this.#url.pathname}${trailingSlash}${stripSlash(value, "start")}`;
    return this;
  }

  /**
   * Sets the search (query string) of the URL.
   * @param {string} search - The search string to set. Should start with '?' if not empty.
   * @returns {this} The UrlBuilder instance for chaining.
   */
  setSearch(search) {
    this.#url.search = search;
    return this;
  }

  /**
   * Sets the host of the URL.
   * @param {string} value - The host to set. This includes the hostname and the port (if specified).
   * @returns {this} The UrlBuilder instance for chaining.
   */
  setHost(value) {
    this.#url.host = value;
    return this;
  }

  /**
   * Sets the origin of the URL.
   * @param {string} value - The origin to set. This should be a valid URL string.
   * @returns {this} The UrlBuilder instance for chaining.
   */
  setOrigin(value) {
    const url = new URL(value);
    this.#url.protocol = url.protocol;
    this.#url.hostname = url.hostname;
    this.#url.port = url.port;
    return this;
  }

  /**
   * Sets the port of the URL.
   * @param {string} value - The port to set. This should be a valid port number as a string.
   * @returns {this} The UrlBuilder instance for chaining.
   */
  setPort(value) {
    this.#url.port = value;
    return this;
  }

  /**
   * Sets the hash (fragment identifier) of the URL.
   * @param {string} value - The hash to set. Should start with '#' if not empty.
   * @returns {this} The UrlBuilder instance for chaining.
   */
  setHash(value) {
    this.#url.hash = value;
    return this;
  }

  // TODO: rewrite instead of .setPath
  // /**
  //  * Sets the pathname of the URL.
  //  * @param {string} value - The pathname to set.
  //  * @returns {this} The UrlBuilder instance for chaining.
  //  */
  // public setPathname(value: string): this {
  //   this.#url.pathname = value;
  //   return this;
  // }

  /**
   * Sets the username of the URL.
   * @param {string} value - The username to set.
   * @returns {this} The UrlBuilder instance for chaining.
   */
  setUsername(value) {
    this.#url.username = value;
    return this;
  }

  /**
   * Sets the password of the URL.
   * @param {string} value - The password to set.
   * @returns {this} The UrlBuilder instance for chaining.
   */
  setPassword(value) {
    this.#url.password = value;
    return this;
  }

  /**
   * Converts the URL to a string representation.
   * This method builds the complete URL and returns it as a string.
   *
   * @public
   * @returns {string} The complete URL as a string.
   */
  toString() {
    return this.build();
  }

  /**
   * Converts the URL to a JSON string representation.
   * This method builds the complete URL, then stringifies it to JSON format.
   *
   * @public
   * @returns {string} The complete URL as a JSON string.
   */
  toJSON() {
    return JSON.stringify(this.build());
  }

  /**
   * Sets the protocol of the URL. "magic method" that invokes
   * @param {ExtendedString<Protocol>} value - The protocol to set (e.g., "http:", "https:").
   * @returns {this} The UrlBuilder instance for chaining.
   */
  setProtocol(value) {
    this.#url.protocol = value;
    return this;
  }

  /**
   * Decodes the URL before returning it.
   * @returns {this} The UrlBuilder instance for chaining.
   */
  decode() {
    this.#shouldDecode = true;
    return this;
  }

  /**
   * Creates a new UrlBuilder instance with the same URL as the current instance.
   * @returns {UrlBuilder} A new UrlBuilder instance with the same URL.
   */
  clone() {
    return new UrlBuilder(this.build());
  }

  /**
   * Builds and returns the complete URL as a string.
   * @description Removes trailing slash from the end of the URL.
   * @returns {string} The complete URL.
   */
  build() {
    return stripSlash(this.href, "end");
  }

  /**
   * Gets the origin of the URL.
   * @description Without trailing slash.
   * @returns {string} The origin of the URL.
   * @see {@link https://developer.mozilla.org/docs/Web/API/URL/origin | MDN Reference}
   */
  get origin() {
    return this.#url.origin;
  }

  /**
   * Gets the host of the URL.
   * @returns {string} The host of the URL.
   * @see {@link https://developer.mozilla.org/docs/Web/API/URL/host | MDN Reference}
   */
  get host() {
    return this.#url.host;
  }

  /**
   * Gets the hostname of the URL.
   * @returns {string} The hostname of the URL.
   * @see {@link https://developer.mozilla.org/docs/Web/API/URL/hostname | MDN Reference}
   */
  get hostname() {
    return this.#url.hostname;
  }

  /**
   * Gets the protocol of the URL.
   * @returns {ExtendedString<Protocol>} The protocol of the URL.
   * @see {@link https://developer.mozilla.org/docs/Web/API/URL/protocol | MDN Reference}
   */
  get protocol() {
    return this.#url.protocol;
  }

  /**
   * Gets the search (query string) of the URL.
   * @returns {string} The search string of the URL.
   * @see {@link https://developer.mozilla.org/docs/Web/API/URL/search | MDN Reference}
   */
  get search() {
    return this.#url.search;
  }

  /**
   * Gets the port of the URL.
   * @returns {string} The port of the URL.
   * @see {@link https://developer.mozilla.org/docs/Web/API/URL/port | MDN Reference}
   */
  get port() {
    return this.#url.port;
  }

  /**
   * Gets the href of the URL.
   * @returns {string} The href of the URL.
   * @see {@link https://developer.mozilla.org/docs/Web/API/URL/href | MDN Reference}
   */
  get href() {
    return this.decodedOrRaw(this.#url.href);
  }

  /**
   * Gets the hash (fragment identifier) of the URL.
   * @returns {string} The hash of the URL.
   * @see {@link https://developer.mozilla.org/docs/Web/API/URL/hash | MDN Reference}
   */
  get hash() {
    return this.decodedOrRaw(this.#url.hash);
  }

  /**
   * Gets the pathname of the URL.
   * @returns {string} The pathname of the URL.
   * @see {@link https://developer.mozilla.org/docs/Web/API/URL/pathname | MDN Reference}
   */
  get pathname() {
    return this.decodedOrRaw(this.#url.pathname);
  }

  /**
   * Gets the searchParams (URLSearchParams) of the URL.
   * @returns {URLSearchParams} The searchParams of the URL.
   * @see {@link https://developer.mozilla.org/docs/Web/API/URL/searchParams | MDN Reference}
   */
  get searchParams() {
    return this.#url.searchParams;
  }

  /**
   * Gets the username of the URL.
   * @returns {string} The username of the URL.
   * @see {@link https://developer.mozilla.org/docs/Web/API/URL/username | MDN Reference}
   */
  get username() {
    return this.decodedOrRaw(this.#url.username);
  }

  /**
   * Gets the password of the URL.
   * @returns {string} The password of the URL.
   * @see {@link https://developer.mozilla.org/docs/Web/API/URL/password | MDN Reference}
   */
  get password() {
    return this.decodedOrRaw(this.#url.password);
  }
  decodedOrRaw(value) {
    return this.#shouldDecode ? decodeURIComponent(value) : value;
  }
}